import React, { useEffect, useRef, useState } from 'react';

import { useHelpBundle } from '@plasma/ui.application.app';
import { useResourceBundle, useTranslation } from '@plasma/ui.utils.i18n';

import { downloadFileGet_byApiName } from '../../services/gets/getdata';
import {
  sendAllExcelExtract,
  sendAllExcelExtractStream,
  sendAllFileByPost,
  ExtractProgressEvent,
} from '../../services/posts/postdata';
import translations from './i18n';
import { UploadExcelProps } from './upload-excel';
import help from './upload-excel.help';

function useUploadExcelHook(props: UploadExcelProps) {
  useResourceBundle(translations, undefined, '723cf97d-866c-4ad4-b24b-bcb8d420665a');
  useHelpBundle(help);

  const { t } = useTranslation();
  const [sheet, setSheet] = useState('');
  const [pass, setPass] = useState('');
  const [responseExcel, setResponseExcel] = useState<string | undefined>();
  const [extractMessage, setExtractMessage] = useState(false);
  const [proce, setProce] = useState(false);
  const [showPassword, setShowPasssword] = useState(false);
  const [sheetOptions, setSheetOptions] = useState<string[]>([]);
  const [sheetNamesLoading, setSheetNamesLoading] = useState(false);
  const [sheetNamesError, setSheetNamesError] = useState('');
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [process, setProcess] = useState(true);
  const [extractSuccess, setExtractSuccess] = useState(true);
  const [progressLog, setProgressLog] = useState<ExtractProgressEvent[]>([]);
  const [progressPercent, setProgressPercent] = useState<number | null>(null);
  const [estimatedSecondsRemaining, setEstimatedSecondsRemaining] = useState<number | null>(null);
  const [estimatedRowsPerSecond, setEstimatedRowsPerSecond] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  // Tracks the in-flight extraction request (if any), so a reset triggered mid-extraction can
  // actually cancel it - without this, the old request's .then()/.catch() would still fire later
  // and could stomp on a freshly-reset (or freshly-restarted) state with stale data.
  const abortControllerRef = useRef<AbortController | null>(null);
  // Rolling window of (arrival time, rows-saved-so-far) samples taken from "Saving" stage events
  // as they stream in - purely client-side, the backend sends no timing/rate info. Used to derive
  // an approximate rows/sec and, from that, an estimated time remaining. A ref rather than state
  // since it's only read/written inside onProgress, never rendered directly.
  const saveRateSamplesRef = useRef<{ time: number; current: number }[]>([]);
  // Wall-clock elapsed time has to keep ticking even during gaps between progress events (e.g. a
  // slow query with no chunk to report yet), so it's driven by its own interval rather than being
  // recomputed only when an event arrives.
  const extractionStartTimeRef = useRef<number | null>(null);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopElapsedTimer = () => {
    if (elapsedTimerRef.current != null) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  };

  // Stop the ticking timer if the component unmounts mid-extraction, so it doesn't keep firing
  // (and trying to setState) after there's nothing left to update.
  useEffect(() => stopElapsedTimer, []);

  const fetchSheetNames = (passwordToTry: string) => {
    setSheetNamesLoading(true);
    setSheetNamesError('');
    sendAllExcelExtract('getExcelSheetNames', {
      companyName: '',
      plantCodeName: '',
      password: passwordToTry,
    })
      .then((response) => {
        if (response?.success) {
          const names: string[] = response.sheetNames ?? [];
          setSheetOptions(names);
          setPasswordRequired(false);
          setSheetNamesError('');
          setSheet(names[0] ?? '');
        } else if (response?.requiresPassword) {
          setSheetOptions([]);
          setPasswordRequired(true);
          setSheetNamesError(response.message || 'This workbook is password protected. Please provide the password.');
        } else {
          setSheetOptions([]);
          setPasswordRequired(false);
          setSheetNamesError(response?.message || 'Failed to read sheet names from the uploaded file.');
        }
      })
      .catch((error) => {
        console.log('Failed to fetch sheet names', error);
        setSheetOptions([]);
        setPasswordRequired(false);
        setSheetNamesError('Failed to read sheet names from the uploaded file.');
      })
      .finally(() => setSheetNamesLoading(false));
  };

  const handleVerifyPassword = () => {
    fetchSheetNames(pass);
  };

  const handleExtractData = () => {
    setProce(true);
    setPass('');
    setSheet('');
    setSheetOptions([]);
    setPasswordRequired(false);
    setSheetNamesError('');
    fetchSheetNames('');
  };

  const handleApiExtract = () => {
    setProce(false);
    setProcess(false);
    setProgressLog([]);
    setProgressPercent(null);
    setEstimatedSecondsRemaining(null);
    setEstimatedRowsPerSecond(null);
    saveRateSamplesRef.current = [];

    extractionStartTimeRef.current = Date.now();
    setElapsedSeconds(0);
    stopElapsedTimer();
    elapsedTimerRef.current = setInterval(() => {
      if (extractionStartTimeRef.current != null) {
        setElapsedSeconds((Date.now() - extractionStartTimeRef.current) / 1000);
      }
    }, 1000);

    const jsonToSend = {
      companyName: '',
      plantCodeName: '',
      sheetName: sheet,
      password: pass,
    };
    console.log(jsonToSend);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Rows are saved across several tables (and, since the backend reloads them concurrently,
    // interleaved with each other) but `current`/`total` on a "Saving" event are always the
    // aggregate rows-saved-so-far / total-rows-to-save across the whole import, so consecutive
    // samples of it still give a meaningful overall rows/sec regardless of which table(s)
    // happened to report in between. Keeping only the most recent samples (rather than averaging
    // from the very start) lets the estimate adapt as throughput changes over the import instead
    // of drifting from one early slow/fast table.
    const MAX_RATE_SAMPLES = 20;
    const MIN_ELAPSED_SECONDS_FOR_ESTIMATE = 0.5;

    const onProgress = (event: ExtractProgressEvent) => {
      setProgressLog((prev) => [...prev, event]);
      if (event.current != null && event.total) {
        setProgressPercent(Math.min(100, Math.round((event.current / event.total) * 100)));
      }

      if (event.stage === 'Saving' && event.current != null && event.total) {
        const now = Date.now();
        const samples = saveRateSamplesRef.current;
        samples.push({ time: now, current: event.current });
        if (samples.length > MAX_RATE_SAMPLES) {
          samples.splice(0, samples.length - MAX_RATE_SAMPLES);
        }

        const remaining = event.total - event.current;
        if (remaining <= 0) {
          setEstimatedSecondsRemaining(0);
        } else {
          const oldest = samples[0];
          const windowElapsedSeconds = (now - oldest.time) / 1000;
          const rowsDelta = event.current - oldest.current;
          if (windowElapsedSeconds >= MIN_ELAPSED_SECONDS_FOR_ESTIMATE && rowsDelta > 0) {
            const rowsPerSecond = rowsDelta / windowElapsedSeconds;
            setEstimatedRowsPerSecond(rowsPerSecond);
            setEstimatedSecondsRemaining(remaining / rowsPerSecond);
          }
        }
      }
    };

    sendAllExcelExtractStream('extractExcelFileStream', jsonToSend, onProgress, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        if (extractionStartTimeRef.current != null) {
          setElapsedSeconds((Date.now() - extractionStartTimeRef.current) / 1000);
        }
        stopElapsedTimer();
        setResponseExcel(String(response?.message ?? 'Extraction finished, but the server did not report a message.'));
        setExtractSuccess(!!response?.success);
        setExtractMessage(true);
        setProgressPercent(100);
        setProcess(true);
      })
      .catch((error) => {
        if (controller.signal.aborted) {
          return;
        }
        if (extractionStartTimeRef.current != null) {
          setElapsedSeconds((Date.now() - extractionStartTimeRef.current) / 1000);
        }
        stopElapsedTimer();
        console.log('Failed to extract data', error);
        setResponseExcel('Extraction failed or timed out. Please try again.');
        setExtractSuccess(false);
        setExtractMessage(true);
        setProcess(true);
      });
  };

  const handleCancel = () => {
    setProce(false);
  };

  const toggleButton = () => {
    setShowPasssword(!showPassword);
  };

  const resetState = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    stopElapsedTimer();
    extractionStartTimeRef.current = null;
    setSheet('');
    setPass('');
    setSheetOptions([]);
    setSheetNamesLoading(false);
    setSheetNamesError('');
    setPasswordRequired(false);
    setResponseExcel(undefined);
    setExtractMessage(false);
    setProce(false);
    setShowPasssword(false);
    setProcess(true);
    setExtractSuccess(true);
    setProgressLog([]);
    setProgressPercent(null);
    setEstimatedSecondsRemaining(null);
    setEstimatedRowsPerSecond(null);
    setElapsedSeconds(0);
    saveRateSamplesRef.current = [];
  };

  // ---------------------------------------------------------------- File selection and upload

  const fileInputRef = useRef<HTMLInputElement>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const [filePath, setFilePath] = useState('');
  const [file, setFile] = useState<FileList | null>(null); // State to hold the selected file
  const [responseSet, setResponseSet] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  // Mirrors abortControllerRef above, but for the plain file-upload request, so Reset can interrupt
  // that phase too instead of only the extract/save stream.
  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  const [resetConfirmVisible, setResetConfirmVisible] = useState(false);

  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [progressLog]);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files; // Get the selected files
    setFile(fileList);
    if (fileList && fileList.length > 0) {
      const fileName = fileList[0].name; // Get the file name
      setFilePath(fileName);
    }
  };

  const handleResetComponent = () => {
    uploadAbortControllerRef.current?.abort();
    uploadAbortControllerRef.current = null;
    setFilePath('');
    setFile(null);
    setResponseSet('');
    setUploading(false);
    setUploadError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    resetState();
  };

  const isProcessRunning = uploading || !process;
  const handleResetClick = () => {
    if (isProcessRunning) {
      setResetConfirmVisible(true);
    } else {
      handleResetComponent();
    }
  };
  const confirmResetInterrupt = () => {
    setResetConfirmVisible(false);
    handleResetComponent();
  };
  const cancelResetInterrupt = () => {
    setResetConfirmVisible(false);
  };

  const handleUpload = async () => {
    if (!file) {
      console.log('No file selected.');
      return;
    }

    const formData = new FormData();

    for (let i = 0; i < file.length; i++) {
      formData.append('File', file[i]);
    }

    formData.forEach((item) => {
      console.log(item);
    });

    setUploadError('');
    setUploading(true);

    const controller = new AbortController();
    uploadAbortControllerRef.current = controller;

    sendAllFileByPost('uploadExcelFile', formData, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        if (response) {
          console.log('File uploaded successfully', response);
          setResponseSet(response);
          setExtractMessage(false);
        } else {
          setUploadError('Upload failed. Please try again.');
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) {
          return;
        }
        console.log(error);
        setUploadError('Upload failed. Please try again.');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setUploading(false);
        }
      });
  };

  // ---------------------------------------------------------------- Download of the uploaded file

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const handleDownloadExcel = async () => {
    setDownloadError('');
    setDownloading(true);
    try {
      await downloadFileGet_byApiName('downloadExcelFile', 'Company_Template.xlsx');
    } catch (error) {
      console.log(error);
      setDownloadError(error instanceof Error && error.message ? error.message : 'Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // ---------------------------------------------------------------- Progress display

  const STAGE_GROUPS: { key: string; label: string; stages: string[] }[] = [
    { key: 'extract', label: 'Extract Excel Data', stages: ['Fetching', 'Parsing'] },
    { key: 'delete', label: 'Delete Master Data', stages: ['Clearing'] },
    { key: 'insert', label: 'Insert Master Data', stages: ['Saving'] },
  ];

  const getStageBarInfo = (
    stages: string[],
  ): { percent: number | null; message: string; started: boolean; active: boolean } => {
    if (!Array.isArray(progressLog) || progressLog.length === 0) {
      return { percent: null, message: '', started: false, active: false };
    }

    let lastMatch: ExtractProgressEvent | undefined;
    for (let i = progressLog.length - 1; i >= 0; i--) {
      if (stages.includes(progressLog[i]?.stage)) {
        lastMatch = progressLog[i];
        break;
      }
    }
    if (!lastMatch) {
      return { percent: null, message: '', started: false, active: false };
    }

    const percent =
      lastMatch.current != null && lastMatch.total
        ? Math.min(100, Math.round((lastMatch.current / lastMatch.total) * 100))
        : null;

    const latestOverall = progressLog[progressLog.length - 1];
    const active = stages.includes(latestOverall?.stage);

    return { percent, message: lastMatch.message ?? '', started: true, active };
  };

  // Shared by the elapsed-time and estimated-time-remaining displays
  const formatDuration = (seconds: number): string => {
    const totalSeconds = Math.max(0, Math.round(seconds));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  // estimatedSecondsRemaining/estimatedRowsPerSecond are derived purely client-side
  // (see handleApiExtract) from the rate of change of the "Saving" stage's row counts across events
  const formatEstimatedTimeRemaining = (seconds: number | null): string | null => {
    if (seconds == null || !Number.isFinite(seconds) || seconds < 0) {
      return null;
    }
    return seconds < 1 ? '~ a few seconds left' : `~${formatDuration(seconds)} left`;
  };

  const formatRowsPerSecond = (rowsPerSecond: number | null): string | null => {
    if (rowsPerSecond == null || !Number.isFinite(rowsPerSecond) || rowsPerSecond <= 0) {
      return null;
    }
    return `${Math.round(rowsPerSecond).toLocaleString()} rows/sec`;
  };

  let activeStep = 0;
  if (extractMessage && extractSuccess) {
    activeStep = 5;
  } else if (extractMessage && !extractSuccess) {
    activeStep = 4;
  } else if (!process) {
    activeStep = 4;
  } else if (proce) {
    activeStep = 3;
  } else if (responseSet !== '' && responseSet !== null && responseSet !== undefined) {
    activeStep = 2;
  } else if (filePath) {
    activeStep = 1;
  }

  return {
    t,
    handleExtractData,
    sheet,
    handleApiExtract,
    handleCancel,
    setSheet,
    pass,
    setPass,
    sheetOptions,
    sheetNamesLoading,
    sheetNamesError,
    passwordRequired,
    handleVerifyPassword,
    extractMessage,
    responseExcel,
    setExtractMessage,
    proce,
    showPassword,
    toggleButton,
    process,
    resetState,
    extractSuccess,
    progressLog,
    progressPercent,
    estimatedSecondsRemaining,
    estimatedRowsPerSecond,
    elapsedSeconds,
    fileInputRef,
    logContainerRef,
    filePath,
    responseSet,
    uploading,
    uploadError,
    resetConfirmVisible,
    handleButtonClick,
    handleFileChange,
    handleResetClick,
    confirmResetInterrupt,
    cancelResetInterrupt,
    handleUpload,
    downloading,
    downloadError,
    handleDownloadExcel,
    STAGE_GROUPS,
    getStageBarInfo,
    formatDuration,
    formatEstimatedTimeRemaining,
    formatRowsPerSecond,
    activeStep,
  };
}

export default useUploadExcelHook;
